import { config } from './config';
import { getCurrentSession } from './auth';

export type Priority = 'low' | 'normal' | 'high';
export type Role = 'backoffice' | 'customer';

export interface SourceOption {
  key: string;
  label: string;
  organizationType?: string;
}

export interface ClientProfile {
  profileId: string;
  companyName: string;
  contactPerson?: string;
  municipalities: string[];
  description: string;
  policyAreas: string;
  keywords: string[];
  exclusions?: string;
  webhookUrl?: string;
  active: boolean;
  createdAt: string;
  updatedAt?: string;
  version?: number;
}

export interface Signal {
  documentId: string;
  profileId: string;
  title: string;
  type: string;
  date: string;
  sourceUrl: string;
  oriIndex: string;
  municipality?: string;
  relevant: boolean;
  priority: Priority;
  reason?: string;
  summary?: string;
  action?: string;
  deadline?: string | null;
  tags?: string[];
  analyzedAt: string;
  sent?: boolean;
  sentAt?: string;
  read?: boolean;
  feedback?: 'up' | 'down' | null;
  feedbackAt?: string;
  profileVersion?: number;
  text?: string;
  gotoToken?: string;
}

export interface SourceText {
  documentId: string;
  title: string;
  date: string;
  type: string;
  sourceUrl: string;
  text: string;
  fetchedAt: string;
}

export interface Run {
  runId: string;
  type: string;
  profileId?: string;
  startedAt: string;
  completedAt?: string;
  status: string;
  details: Record<string, unknown>;
  triggeredBy?: string;
}

export interface ProfileVersion {
  profileId: string;
  version: number;
  profile: ClientProfile;
  modifiedBy: string;
  modifiedAt: string;
}

async function call<T>(path: string, init: RequestInit = {}): Promise<T> {
  const session = await getCurrentSession();
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(init.headers as Record<string, string> | undefined),
  };
  if (config.localAuth && session?.localPersona) {
    headers['X-Local-Auth'] = session.localPersona;
  } else if (session?.idToken) {
    headers['Authorization'] = `Bearer ${session.idToken}`;
  }
  const response = await fetch(`${config.apiUrl}${path}`, { ...init, headers });
  if (!response.ok) {
    const text = await response.text();
    let message = text;
    try {
      const parsed = JSON.parse(text);
      message = parsed.error ?? text;
    } catch {}
    throw new Error(`${response.status}: ${message}`);
  }
  if (response.status === 204) {
    return undefined as T;
  }
  return response.json() as Promise<T>;
}

export interface UsageRecord {
  profileId: string;
  yyyymm: string;
  inputTokens: number;
  outputTokens: number;
  callCount: number;
}

export interface CustomerUser {
  userId: string;
  email: string;
  role: Role;
  createdAt: string;
  lastLogin?: string;
  profileIds: string[];
  status?: string;
}

export interface Collection {
  collectionId: string;
  profileId: string;
  name: string;
  description?: string;
  createdAt: string;
  createdBy?: string;
  updatedAt?: string;
}

export const api = {
  me: () => call<{ sub: string; email: string; role: Role }>('/me'),
  myProfiles: () => call<ClientProfile[]>('/me/profiles'),

  listClients: () => call<ClientProfile[]>('/profiles'),
  listSources: () => call<SourceOption[]>('/sources'),
  getClient: (id: string) => call<ClientProfile>(`/profiles/${id}`),
  createClient: (body: Partial<ClientProfile>) =>
    call<ClientProfile>('/profiles', { method: 'POST', body: JSON.stringify(body) }),
  updateClient: (id: string, body: Partial<ClientProfile>) =>
    call<ClientProfile>(`/profiles/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),
  deleteClient: (id: string) => call<{ deleted: boolean }>(`/profiles/${id}`, { method: 'DELETE' }),
  listVersions: (id: string) => call<ProfileVersion[]>(`/profiles/${id}/versions`),

  triggerTestrun: (id: string, body: { hours?: number; municipalities?: string[]; max?: number; dryRun?: boolean }) =>
    call<{ runId: string; status: string; local?: boolean }>(`/profiles/${id}/testrun`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  listSignals: (
    id: string,
    params: {
      from?: string;
      to?: string;
      onlyRelevant?: boolean;
      priority?: string;
      municipality?: string;
      tag?: string;
      status?: 'read' | 'unread';
      q?: string;
      limit?: number;
    } = {},
  ) => {
    const qs = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        qs.set(key, String(value));
      }
    });
    const suffix = qs.toString();
    return call<Signal[]>(`/profiles/${id}/signals${suffix ? '?' + suffix : ''}`);
  },
  getSignal: (profileId: string, docId: string) => call<Signal>(`/profiles/${profileId}/signals/${docId}`),
  patchSignal: (profileId: string, docId: string, patch: { read?: boolean; feedback?: 'up' | 'down' | null }) =>
    call<Signal>(`/profiles/${profileId}/signals/${docId}`, {
      method: 'PATCH',
      body: JSON.stringify(patch),
    }),
  getSourceText: (profileId: string, docId: string) =>
    call<SourceText>(`/profiles/${profileId}/signals/${docId}/brontekst`),

  listRuns: (type?: string) => call<Run[]>(type ? `/runs?type=${type}` : '/runs'),
  getRun: (id: string) => call<Run>(`/runs/${id}`),

  // Customer-user beheer (backoffice)
  listCustomerUsers: () => call<CustomerUser[]>('/users'),
  createCustomerUser: (body: { email: string; profileIds?: string[] }) =>
    call<CustomerUser>('/users', { method: 'POST', body: JSON.stringify(body) }),
  updateCustomerUser: (userId: string, body: { profileIds: string[] }) =>
    call<{ updated: boolean }>(`/users/${userId}`, { method: 'PATCH', body: JSON.stringify(body) }),
  deleteCustomerUser: (userId: string) => call<{ deleted: boolean }>(`/users/${userId}`, { method: 'DELETE' }),
  resetCustomerUserPassword: (userId: string) =>
    call<{ reset: boolean }>(`/users/${userId}/reset-password`, { method: 'POST' }),

  // Token-verbruik (backoffice)
  listUsage: () => call<UsageRecord[]>('/usage'),
  listUsageForClient: (profileId: string) => call<UsageRecord[]>(`/profiles/${profileId}/usage`),

  // Collections (n:n met signals)
  listCollections: (profileId: string) => call<Collection[]>(`/profiles/${profileId}/collections`),
  getCollection: (profileId: string, collectionId: string) =>
    call<Collection>(`/profiles/${profileId}/collections/${collectionId}`),
  createCollection: (profileId: string, body: { name: string; description?: string }) =>
    call<Collection>(`/profiles/${profileId}/collections`, { method: 'POST', body: JSON.stringify(body) }),
  updateCollection: (profileId: string, collectionId: string, body: { name?: string; description?: string }) =>
    call<Collection>(`/profiles/${profileId}/collections/${collectionId}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),
  deleteCollection: (profileId: string, collectionId: string) =>
    call<{ deleted: boolean }>(`/profiles/${profileId}/collections/${collectionId}`, { method: 'DELETE' }),
  listCollectionSignals: (
    profileId: string,
    collectionId: string,
    params: {
      from?: string;
      to?: string;
      priority?: string;
      municipality?: string;
      tag?: string;
      status?: 'read' | 'unread';
      q?: string;
    } = {},
  ) => {
    const qs = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        qs.set(key, String(value));
      }
    });
    const suffix = qs.toString();
    return call<Signal[]>(`/profiles/${profileId}/collections/${collectionId}/signals${suffix ? '?' + suffix : ''}`);
  },
  addSignalToCollection: (profileId: string, collectionId: string, docId: string) =>
    call<{ added: boolean }>(`/profiles/${profileId}/collections/${collectionId}/signals/${docId}`, {
      method: 'POST',
    }),
  removeSignalFromCollection: (profileId: string, collectionId: string, docId: string) =>
    call<{ removed: boolean }>(`/profiles/${profileId}/collections/${collectionId}/signals/${docId}`, {
      method: 'DELETE',
    }),
  listCollectionsForSignal: (profileId: string, docId: string) =>
    call<string[]>(`/profiles/${profileId}/signals/${docId}/collections`),

  // Members per profile (backoffice)
  listMembers: (profileId: string) => call<CustomerUser[]>(`/profiles/${profileId}/members`),
  addMember: (profileId: string, body: { userId?: string; email?: string }) =>
    call<unknown>(`/profiles/${profileId}/members`, { method: 'POST', body: JSON.stringify(body) }),
  removeMember: (profileId: string, userId: string) =>
    call<{ removed: boolean }>(`/profiles/${profileId}/members/${userId}`, { method: 'DELETE' }),
};
